import { BILLING_PLAN, v2TierFor, type PricingEdition } from '@highlight/common/types'

/**
 * User store key. Set once the toast has been shown, or once the account was seen on a plan outside
 * the paid v2 tiers after the flip, so no later upgrade is mistaken for the revamp landing.
 */
export const PLAN_LIVE_TOAST_SEEN_KEY = 'hasSeenPlanLiveToast'

export const PLAN_LIVE_TOAST_ID = 'plan-live'

/**
 * Which announcement a user gets. Pro gained custom skills, so both Pro variants offer one; a
 * user the revamp moved up from Lite is welcomed rather than told their plan changed. Max already
 * had custom skills, so it hears about its larger allowances instead.
 */
export type PlanLiveToastVariant = 'pro' | 'pro-from-lite' | 'max'

export interface PlanLiveToastInput {
  /** Stores still hold defaults until IPC is ready, and a default `hasSeen` of false must not fire the toast. */
  isIpcReady: boolean
  /**
   * The billing snapshot was fetched during this launch. The store persists across restarts, so an
   * older snapshot can show a plan the user no longer holds.
   */
  billingFresh: boolean
  /** The CTA's prompt is chosen from the user's connections, so the toast waits for them to load or fail. */
  connectionsSettled: boolean
  edition: PricingEdition
  plan: string | null | undefined
  /** The plan held before the current one. The revamp migration carries Lite here when it moves a user to Pro. */
  previousPlan: string | null | undefined
  hasSeen: boolean
}

export type PlanLiveToastDecision =
  | { action: 'show'; variant: PlanLiveToastVariant }
  /**
   * Found on Free or a legacy plan after the flip, so the announcement is not for this account, now or after an upgrade.
   * Lite is excluded because it waits for its move to Pro.
   */
  | { action: 'mark-seen' }
  | null

const LITE_PLANS: ReadonlySet<string> = new Set([BILLING_PLAN.LITE, BILLING_PLAN.LITE_TRIAL])

/**
 * The announcement is only true once the flag says `v2-revamp`, and only for subscribers who were
 * already on a paid v2 tier, trials included, when it flipped. Anyone found outside those tiers
 * with the flag on is marked seen instead, so a trial or purchase they start later stays quiet.
 * Lite waits rather than being marked seen. Null means nothing to do yet.
 */
export function decidePlanLiveToast({
  isIpcReady,
  billingFresh,
  connectionsSettled,
  edition,
  plan,
  previousPlan,
  hasSeen,
}: PlanLiveToastInput): PlanLiveToastDecision {
  if (!isIpcReady || !billingFresh || hasSeen || edition !== 'v2-revamp') return null
  const variant = variantFor(plan, previousPlan)
  if (!variant) {
    // Lite is a paid plan the revamp moves to Pro, so it waits for the move rather than being stamped.
    if (plan && LITE_PLANS.has(plan)) return null
    return { action: 'mark-seen' }
  }
  if (!connectionsSettled) return null
  return { action: 'show', variant }
}

function variantFor(plan: string | null | undefined, previousPlan: string | null | undefined) {
  switch (v2TierFor(plan)) {
    case 'pro2':
      return previousPlan && LITE_PLANS.has(previousPlan) ? 'pro-from-lite' : 'pro'
    case 'max':
      return 'max'
    default:
      return null
  }
}
